import {
  Button,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@growdash/design-system/atoms';
import { Info } from 'lucide-react';
import React from 'react';

interface PlanInfoCardElement {
  title: string;
  value: string;
  info?: string;
  color?: string;
}

interface InsideCardProps {
  title: string;
  value: string;
  icon: React.ReactNode;
  date: string;
}

interface PlanInfoCardProps {
  title: string;
  headerInfo?: string;
  elements?: PlanInfoCardElement[];
  insideCard?: InsideCardProps;
  buttonText?: string;
  onButtonClįck?: () => void; // Corrected prop name to follow camelCase
  secondButtonText?: string; // Corrected prop name
  onSecondButtonClįck?: () => void; // Corrected prop name
  secondButtonLoading?: boolean;
  secondButtonType?: 'default' | 'destructive';
}

function PlanInfoCard({
  title,
  headerInfo,
  elements,
  insideCard,
  buttonText,
  onButtonClįck,
  secondButtonText,
  onSecondButtonClįck,
  secondButtonLoading,
  secondButtonType
}: PlanInfoCardProps) {
  return (
    <Card className='w-full '>
      <CardHeader className='flex flex-row justify-between items-center border-b border-muted-foreground/50 py-2 min-h-[53px]'>
        <CardTitle>{title}</CardTitle>
        {headerInfo && (
          <div className='border-[1.5px] border-white bg-[linear-gradient(90.96deg,rgba(120,190,233,0.11)_-9.1%,rgba(255,255,255,0.5)_100.85%)] shadow-[0px_5px_7px_0px_#DFDFDF47,inset_0px_0px_17px_0px_#78BEE926,0px_0px_2px_0px_#78BEE9] text-[#8D73EE] dark:text-[#FFFFFF] px-3 py-1 font-semibold rounded-full text-sm'>
            {headerInfo}
          </div>
        )}
      </CardHeader>
      <CardContent className='space-y-6 pt-2'>
        <div className='grid grid-cols-2 gap-x-4 gap-y-2 text-sm'>
          {elements?.map(element => (
            <div className='flex flex-col' key={element.title}>
              <div className='text-[#0A143A] dark:text-[#FFFFFF] text-[14px] font-bold'>
                {element.title}
              </div>
              <div className='text-right dark:text-white'>
                <div className='flex items-center text-left gap-2 text-[#0A143A] dark:text-[#FFFFFF] text-[14px] font-normal'>
                  <span
                    className={`${element.color} ${
                      element.color === 'text-red-500'
                        ? 'text-red-500'
                        : element.color === 'text-green-500'
                          ? 'text-green-500'
                          : 'text-[#0A143A] dark:text-[#FFFFFF]'
                    }`}
                  >
                    {element.value}
                  </span>
                  {element.info && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className='w-4 h-4 text-muted-foreground' />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>{element.info}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {insideCard && (
          <Card className='bg-blue-100 dark:bg-[#1A1A1A] p-4'>
            <div className='flex items-start space-x-3'>
              <div className='flex-shrink-0 text-primary'>
                {insideCard.icon}
              </div>
              <div className='flex-1 space-y-1'>
                <p className='text-xs text-[#0A143A] dark:text-[#FFFFFF] uppercase font-semibold'>
                  {insideCard.title}
                </p>
                <p className='text-xl font-semibold text-[20px] text-[#0A143A] dark:text-[#FFFFFF]'>
                  {insideCard.value}
                </p>
                <p className='text-xs text-[#494B54] dark:text-[#FFFFFF]'>
                  {insideCard.date}
                </p>
              </div>
            </div>
          </Card>
        )}
      </CardContent>
      <CardFooter className='flex space-x-2'>
        {buttonText && onButtonClįck && (
          <Button
            variant='outline'
            onClick={onButtonClįck}
            className=' border-[#E1E1EC] text-[#72778A] text-[14px] font-semibold'
          >
            {buttonText}
          </Button>
        )}
        {secondButtonText && onSecondButtonClįck && (
          <Button
            className={` text-[14px] font-semibold ${
              secondButtonType === 'destructive'
                ? 'text-red-500'
                : 'text-[#72778A]'
            }`}
            onClick={onSecondButtonClįck}
            variant='ghost'
            disabled={secondButtonLoading}
          >
            {secondButtonText}
          </Button>
        )}
      </CardFooter>
    </Card>
  );
}

export default PlanInfoCard;
